import React from 'react'
import { FormikProps } from 'formik'
import { IntlShape } from 'react-intl'
import { TourDetails, TourRouteWaypoint } from '../core/_models'

type Props = {
  formik: FormikProps<TourDetails>
  intl: IntlShape
}

function normalizeWaypoints(list: TourRouteWaypoint[] | undefined): TourRouteWaypoint[] {
  if (!Array.isArray(list)) return []
  return list.map((w, i) => ({
    order: typeof w?.order === 'number' ? w.order : i + 1,
    title: w?.title ?? '',
    lat: typeof w?.lat === 'number' ? w.lat : Number(w?.lat) || 0,
    lng: typeof w?.lng === 'number' ? w.lng : Number(w?.lng) || 0,
  }))
}

export const RouteWaypointsEditor: React.FC<Props> = ({ formik, intl }) => {
  const rows = normalizeWaypoints(formik.values.routeWaypoints)

  const setRows = (next: TourRouteWaypoint[]) => {
    formik.setFieldValue(
      'routeWaypoints',
      next.map((w, i) => ({ ...w, order: i + 1 })),
    )
  }

  const addEmpty = () => {
    const lat = Number(formik.values.location?.mapLatitude) || 0
    const lng = Number(formik.values.location?.mapLongitude) || 0
    setRows([...rows, { order: rows.length + 1, title: '', lat, lng }])
  }

  const addFromPin = () => {
    const lat = Number(formik.values.location?.mapLatitude)
    const lng = Number(formik.values.location?.mapLongitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }
    setRows([...rows, { order: rows.length + 1, title: '', lat, lng }])
  }

  const removeAt = (index: number) => {
    setRows(rows.filter((_, i) => i !== index))
  }

  const move = (index: number, dir: -1 | 1) => {
    const j = index + dir
    if (j < 0 || j >= rows.length) return
    const copy = [...rows]
    ;[copy[index], copy[j]] = [copy[j], copy[index]]
    setRows(copy)
  }

  const updateRow = (index: number, patch: Partial<TourRouteWaypoint>) => {
    const copy = rows.map((r, i) => (i === index ? { ...r, ...patch } : r))
    setRows(copy)
  }

  return (
    <div className="card card-flush py-4 mt-10">
      <div className="card-header">
        <div className="card-title">
          <h2>
            {intl.formatMessage({
              id: 'TOUR.ROUTE.TITLE',
              defaultMessage: 'Route waypoints (map polyline)',
            })}
          </h2>
        </div>
      </div>
      <div className="card-body pt-0">
        <p className="text-muted small mb-5">
          {intl.formatMessage({
            id: 'TOUR.ROUTE.HINT',
            defaultMessage:
              'Add at least two points with valid coordinates to draw a straight line on the public tour map. Use Save at the bottom of this page to persist waypoints with the tour.',
          })}
        </p>
        <p className="text-muted small mb-5 border-start border-3 border-primary ps-3">
          {intl.formatMessage({
            id: 'TOUR.ROUTE.CITY_SEARCH_HINT',
            defaultMessage:
              'Le libellé ici est libre (ex. nom affiché sur la carte). Pour que les clients trouvent le circuit par ville dans la recherche, renseignez les villes référencées dans l’onglet Général → « Villes du parcours (recherche) ».',
          })}
        </p>
        <div className="d-flex flex-wrap gap-2 mb-5">
          <button type="button" className="btn btn-sm btn-light-primary" onClick={addEmpty}>
            {intl.formatMessage({ id: 'TOUR.ROUTE.ADD_ROW', defaultMessage: 'Add waypoint' })}
          </button>
          <button type="button" className="btn btn-sm btn-light" onClick={addFromPin}>
            {intl.formatMessage({
              id: 'TOUR.ROUTE.ADD_FROM_PIN',
              defaultMessage: 'Add from map pin',
            })}
          </button>
        </div>
        {rows.length === 0 ? (
          <p className="text-muted">
            {intl.formatMessage({
              id: 'TOUR.ROUTE.EMPTY',
              defaultMessage: 'No waypoints yet.',
            })}
          </p>
        ) : (
          <div className="table-responsive">
            <table className="table table-row-bordered align-middle">
              <thead>
                <tr className="text-start text-muted fw-bold fs-7 text-uppercase">
                  <th>#</th>
                  <th>{intl.formatMessage({ id: 'TOUR.ROUTE.COL_TITLE', defaultMessage: 'Label' })}</th>
                  <th>Lat</th>
                  <th>Lng</th>
                  <th className="text-end">
                    {intl.formatMessage({ id: 'TOUR.ROUTE.COL_ACTIONS', defaultMessage: 'Actions' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`wp-${index}`}>
                    <td className="text-muted">{index + 1}</td>
                    <td style={{ minWidth: 140 }}>
                      <input
                        type="text"
                        className="form-control form-control-sm form-control-solid"
                        value={row.title}
                        onChange={(e) => updateRow(index, { title: e.target.value })}
                      />
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm form-control-solid"
                        value={row.lat}
                        onChange={(e) => updateRow(index, { lat: parseFloat(e.target.value) })}
                      />
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm form-control-solid"
                        value={row.lng}
                        onChange={(e) => updateRow(index, { lng: parseFloat(e.target.value) })}
                      />
                    </td>
                    <td className="text-end text-nowrap">
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light me-1"
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        aria-label="up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light me-1"
                        onClick={() => move(index, 1)}
                        disabled={index === rows.length - 1}
                        aria-label="down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="btn btn-icon btn-sm btn-light-danger"
                        onClick={() => removeAt(index)}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 pt-6 border-top">
          <h5 className="mb-4">
            {intl.formatMessage({
              id: 'TOUR.ROUTE.SCHEDULE.TITLE',
              defaultMessage: 'Global schedule',
            })}
          </h5>
          <p className="text-muted small mb-4">
            {intl.formatMessage({
              id: 'TOUR.ROUTE.SCHEDULE.HINT',
              defaultMessage:
                'These times are for the whole tour (not a single itinerary element).',
            })}
          </p>
          <div className="row g-4">
            <div className="col-md-6">
              <label className="form-label">
                {intl.formatMessage({ id: 'TOUR.FIELDS.START_TIME' })}
              </label>
              <input
                type="time"
                className="form-control form-control-sm form-control-solid"
                value={formik.values.departureTime || ''}
                onChange={(e) => formik.setFieldValue('departureTime', e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">
                {intl.formatMessage({ id: 'TOUR.FIELDS.END_TIME' })}
              </label>
              <input
                type="time"
                className="form-control form-control-sm form-control-solid"
                value={formik.values.arrivalTime || ''}
                onChange={(e) => formik.setFieldValue('arrivalTime', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
